import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useLazyQuery } from '@grandvisionhq/graphql';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { isNil } from '@grandvisionhq/utils';
import { toCartView } from '../mappers/view/to-cart-view.js';
import { getFailureCodes, mapCartError } from '../utils/get-failure-error-codes.js';
import { useCartStorage } from './use-cart-storage.js';
import { useCartSettings } from '../context/CartSettings/context.js';
import { getUnrecoverableValidationErrors } from '../utils/get-unrecoverable-validation-errors.js';
import { GET_CART } from '../operations/queries/get-cart.js';

const useGetCart = ()=>{
    const settings = useCartSettings();
    const intl = useIntl();
    const { isClearCartOnAllErrorsEnabled  } = useFeatureToggles();
    const [cart, { setCartId , setCartHash , reset  }] = useCartStorage();
    const { cartId , cartHash , setContext  } = settings;
    const cartIdRef = cartId || cart.cartId;
    // This is needed to prefill the cart with the customer logic in the back-office flow.
    if (cartId && cartHash && (cart.cartId !== cartId || cart.cartHash !== cartHash)) {
        setCartId(cartId);
        setCartHash(cartHash);
    }
    const [getCart, { called , loading , data: data1 , error: error1 , refetch  }] = useLazyQuery(GET_CART, {
        onCompleted: (data)=>{
            var ref, ref1;
            const failures = data === null || data === void 0 ? void 0 : (ref = data.cart) === null || ref === void 0 ? void 0 : ref.failures;
            const validationErrors = data === null || data === void 0 ? void 0 : (ref1 = data.cart) === null || ref1 === void 0 ? void 0 : ref1.validationErrors;
            if (getUnrecoverableValidationErrors(validationErrors).length || (failures === null || failures === void 0 ? void 0 : failures.length)) {
                if (failures) {
                    getFailureCodes(failures).forEach((failureCode)=>{
                        console.error('cart error', failureCode);
                    });
                    toast.failed('cart.errors.failedToLoadCart');
                }
                // If there are unsolvable validation errors or failures, reset the cart
                reset();
            }
        },
        onError: (error)=>{
            console.error(error);
            if (isClearCartOnAllErrorsEnabled) {
                reset();
            }
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        context: {
            forwardHeaders: {
                'cart-hash': cart.cartHash
            }
        }
    });
    const refetchCallback = React.useCallback(()=>{
        if (cartIdRef) {
            // Refetch is undefined if the initial run has not been executed
            refetch ? refetch({
                id: cartIdRef
            }) : getCart({
                variables: {
                    id: cartIdRef
                }
            });
        }
    }, [
        getCart,
        refetch,
        cartIdRef
    ]);
    React.useEffect(()=>{
        if (cartIdRef) {
            getCart({
                variables: {
                    id: cartIdRef
                }
            });
        }
    }, [
        getCart,
        cartIdRef
    ]);
    const fetchByCartId = React.useCallback((cartIdParam, cartHashParam)=>{
        // Set cartId
        setCartId(cartIdParam);
        // Set carthash in localstorage
        if (cartHashParam) {
            setCartHash(cartHashParam);
        }
        // update react context with latest cartId & carthash
        setContext && setContext(swcHelpers.objectSpread({
            cartId: cartIdParam
        }, cartHashParam && {
            cartHash: cartHashParam
        }));
        // fetch cart based on latest cartId & carthash without relying on localstorage or context
        if (cartIdParam) {
            refetch ? refetch({
                id: cartIdParam
            }) : getCart(swcHelpers.objectSpread({
                variables: {
                    id: cartIdParam
                }
            }, cartHashParam && {
                context: {
                    forwardHeaders: {
                        'cart-hash': cartHashParam
                    }
                }
            }));
        }
    }, [
        setCartId,
        setCartHash,
        setContext,
        refetch,
        getCart
    ]);
    const mappedCart = React.useMemo(()=>{
        var ref;
        if (!data1) return data1;
        if (isNil(data1 === null || data1 === void 0 ? void 0 : data1.cart.cart)) {
            return null;
        }
        return toCartView(data1 === null || data1 === void 0 ? void 0 : data1.cart.cart, data1 === null || data1 === void 0 ? void 0 : (ref = data1.cart) === null || ref === void 0 ? void 0 : ref.validationErrors, settings, intl);
    }, [
        data1,
        intl,
        settings
    ]);
    return React.useMemo(()=>{
        var ref;
        return {
            called,
            loading,
            data: mappedCart,
            error: !!(data1 === null || data1 === void 0 ? void 0 : (ref = data1.cart) === null || ref === void 0 ? void 0 : ref.failures) ? mapCartError(data1 === null || data1 === void 0 ? void 0 : data1.cart.failures) : error1,
            refetch: refetchCallback,
            fetchByCartId,
            reset
        };
    }, [
        called,
        mappedCart,
        data1,
        error1,
        fetchByCartId,
        loading,
        refetchCallback,
        reset
    ]);
};

export { useGetCart };
//# sourceMappingURL=use-get-cart.js.map
